// src/components/purchasing/SendOrderSection.js
// Session 78n8.2, Issue #395 (+ #257). Gate Screen 2.
//
// WHAT THIS IS, in plain words: the bottom of the Mark as ordered sheet. Jennifer
// has just filled in the vendor, the prices and the quantities. This is where she
// says how the order should reach the vendor, and it is the last thing she touches
// before pressing Confirm.
//
// WHY IT IS ITS OWN FILE: PurchasingQueuePage.js is already 3,400 lines and has a
// decomposition sprint booked against it (#148 Split C, Oct 12-14). Adding another
// 250 lines to MarkOrderedSheet would make that sprint worse. It also keeps locked
// rule 2 easy to honour -- this is a module-level component, never a function
// defined inside a parent's body, which would remount on every render and steal
// focus from the note field on every keystroke.
//
// NO position:fixed ANYWHERE IN HERE, per locked rule 3. The preview expands
// INLINE rather than opening as a modal, which is deliberate twice over: it
// avoids adding a seventh #148 instance to this file, and it is the same call
// 78n8 made for #410's confirmation number. A preview is not an interruption.

import React, { useMemo, useState } from 'react';
import {
  buildPurchaseOrder, renderPurchaseOrderHtml, buildMailtoLink, money,
} from '../../lib/purchaseOrder';
import { downloadPurchaseOrderPdf } from '../../lib/purchaseOrderPdf';

const ACCENT = '#EF9F27';
const INK = '#0F0F0F';
const MUTED = '#5C5C5C';
const LINE = '#E0E0E0';

// The three routes, in the words of the outcome rather than the mechanism.
// Gate callout H: the choice a person is actually making is whether Stede sends
// it or hands them a draft -- not "server-side send" versus "mailto".
// Each carries a HINT as well as a label, on Colby's instruction at 78n8.2:
// *"put some kind of a helper text in with the pathways that help purchasing
// know when to utilize each pathway... mainly want her to know when to use the
// dont send and that it's designed for phone orders."*
//
// The hint sits ON the button rather than under the selected one, and that is
// the whole point of the change: an explanation that only appears AFTER she has
// chosen answers the question too late. She is picking between three routes, so
// all three have to say what they are for while she is looking at them.
//
// They are deliberately three or four words. This row is three buttons wide on a
// 375px phone -- roughly 115px each -- and a sentence would wrap to four lines
// and push the note field off the screen.
const MODES = [
  { key: 'send',  label: 'Send it for me',    hint: 'The usual way' },
  { key: 'draft', label: 'Draft in my email', hint: 'You send it' },
  { key: 'none',  label: "Don't send",        hint: 'Phone orders' },
];

function Row({ label, value, warn }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '5px 0' }}>
      <div style={{ fontSize: 11, color: MUTED, minWidth: 92, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 13, color: warn ? '#A32D2D' : INK, wordBreak: 'break-word', flex: 1 }}>{value}</div>
    </div>
  );
}

export default function SendOrderSection({
  vendor, branch, settings, buyer, lines,
  poRef, requestNumber, workOrderRef, requesterName,
  mode, setMode, note, setNote,
  sendPo, setSendPo, sendConfirmation, setSendConfirmation,
  blockers,
  disabled, isMobile,
}) {
  const [showPreview, setShowPreview] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const order = useMemo(() => buildPurchaseOrder({
    poRef, requestNumber, workOrderRef,
    vendor, branch, settings, buyer, lines, note,
    orderedAt: new Date(),
  }), [poRef, requestNumber, workOrderRef, vendor, branch, settings, buyer, lines, note]);

  // Passed IN rather than computed here, since Session 78n8.2's live test: the
  // Confirm button's own label depends on whether the send is blocked, and that
  // button lives in the parent. Two components computing "is this sendable"
  // separately is two answers that can disagree -- and the one that would have
  // disagreed is the button, which promised a send it could not perform.

  const mailto = useMemo(() => buildMailtoLink(order), [order]);
  const blocked = (blockers || []).length > 0;

  async function handleDownload() {
    setPdfBusy(true);
    setPdfError(null);
    try {
      await downloadPurchaseOrderPdf(order);
    } catch (e) {
      console.error('[#395] PDF build failed', e);
      setPdfError('The PDF could not be built. The order can still be marked as placed.');
    } finally {
      setPdfBusy(false);
    }
  }

  const pill = (active) => ({
    flex: 1,
    padding: isMobile ? '11px 8px' : '9px 10px',
    borderRadius: 9,
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    border: active ? `1.5px solid ${ACCENT}` : `1px solid ${LINE}`,
    background: active ? '#FDF3E3' : '#fff',
    color: active ? '#633806' : MUTED,
    textAlign: 'center',
  });

  return (
    <div style={{ borderTop: `1px solid ${LINE}`, marginTop: 4, paddingTop: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
        Send the order
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            type="button"
            disabled={disabled || (blocked && m.key === 'send')}
            onClick={() => setMode(m.key)}
            style={{
              ...pill(mode === m.key),
              ...(blocked && m.key === 'send'
                ? { background: '#F5F5F3', color: '#9A9A9A', border: `1px solid ${LINE}`, cursor: 'not-allowed' }
                : {}),
            }}
          >
            <div>{m.label}</div>
            <div style={{
              fontSize: 10.5,
              fontWeight: 400,
              marginTop: 3,
              lineHeight: 1.3,
              color: (blocked && m.key === 'send') ? '#B0B0B0' : (mode === m.key ? '#8A5A00' : '#9A9A9A'),
            }}>
              {m.hint}
            </div>
          </button>
        ))}
      </div>

      {/* Gate callout G. BLOCKED with the reason visible, never refused after
          the click: submitMarkOrdered's error renders at the very top of this
          sheet -- measured at 1024px above the button on a laptop -- so a
          refusal reads as a dead button and the explanation is a screen and a
          half away. Four other forms in this file already needed this rule. */}
      {blocked && (
        <div style={{ background: '#FDECEC', border: '1px solid #E9B7B7', borderRadius: 9, padding: '10px 12px', marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#8A2020', marginBottom: 4 }}>
            Stede cannot send this order yet
          </div>
          {(blockers || []).map((b, i) => (
            <div key={i} style={{ fontSize: 12, color: '#8A2020', lineHeight: 1.5 }}>• {b}</div>
          ))}
          <div style={{ fontSize: 11.5, color: '#8A2020', marginTop: 6, lineHeight: 1.5 }}>
            The order can still be marked as placed — only the send is unavailable.
          </div>
        </div>
      )}

      {mode === 'send' && !blocked && (
        <div style={{ background: '#FAFAF8', border: `1px solid ${LINE}`, borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
          {/* Gate callout I: From, Replies go to and Copy to are all SHOWN. This
              is the first time Stede emails somebody outside the company, and
              the existing sender carries no reply-to at all -- so the identity
              is checkable at a glance instead of trusted. */}
          <Row label="To" value={vendor?.email} />
          <Row label="From" value={`${buyer?.full_name || 'You'} · ${settings?.company_name || settings?.legal_name || 'D&R Signs'}`} />
          <Row label="Replies to" value={buyer?.email} />
          <Row label="Copy to" value={`${buyer?.email} — so the sent order is in your mailbox too`} />
        </div>
      )}

      {mode === 'send' && !blocked && (
        <>
          {/* Gate callout J. The personal line was the real reason to want a
              draft in the first place. Once "no rush, this is for the Whitfield
              job" can be added without leaving the sheet, the draft route stops
              being the one she reaches for by default. */}
          <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
            Add a note to the vendor <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
          </div>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            disabled={disabled}
            rows={2}
            placeholder="Dana — this one's for the Whitfield job, no rush."
            style={{
              width: '100%', border: `1px solid ${LINE}`, borderRadius: 9, padding: '10px 12px',
              // 16px minimum, locked rule 12 -- anything smaller makes iOS zoom
              // the whole sheet on focus.
              fontSize: 16, color: INK, boxSizing: 'border-box', marginBottom: 12,
              fontFamily: 'inherit', resize: 'vertical',
            }}
          />

          {/* Gate callout K: both documents are one action, so there is no
              second screen to forget. */}
          <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 8, cursor: disabled ? 'not-allowed' : 'pointer' }}>
            <input type="checkbox" checked={sendPo} disabled={disabled}
              onChange={e => setSendPo(e.target.checked)} style={{ marginTop: 2, width: 17, height: 17 }} />
            <span style={{ fontSize: 13, color: INK, lineHeight: 1.45 }}>
              Purchase order to {vendor?.name || 'the vendor'}
              <span style={{ color: MUTED }}> — email with the PDF attached</span>
            </span>
          </label>

          <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 12, cursor: disabled ? 'not-allowed' : 'pointer' }}>
            <input type="checkbox" checked={sendConfirmation} disabled={disabled}
              onChange={e => setSendConfirmation(e.target.checked)} style={{ marginTop: 2, width: 17, height: 17 }} />
            <span style={{ fontSize: 13, color: INK, lineHeight: 1.45 }}>
              Confirmation to {requesterName || 'the requester'}
              <span style={{ color: MUTED }}> — in-app and email</span>
            </span>
          </label>
        </>
      )}

      {mode === 'draft' && (
        <div style={{ background: '#FAFAF8', border: `1px solid ${LINE}`, borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
          <div style={{ fontSize: 12.5, color: INK, lineHeight: 1.5 }}>
            Confirming opens your own mail app with the order already written, addressed to {vendor?.email || 'the vendor'}. You send it yourself, so it sits in your Sent folder.
          </div>
          {/* F4, and this warning is the whole reason the draft route is second
              rather than first. A mailto body is plain text in every major mail
              client, and Outlook simply does not open the link past about a
              thousand characters -- which fails as "the button did nothing"
              rather than as an error. Said BEFORE she picks it, not after. */}
          {mailto.tooLongForOutlook && (
            <div style={{ fontSize: 12, color: '#8A5A00', background: '#FDF3E3', border: `1px solid ${ACCENT}`, borderRadius: 8, padding: '8px 10px', marginTop: 8, lineHeight: 1.5 }}>
              This order is long enough that Outlook may not open the draft at all, and it will look like nothing happened. A plain-text draft also cannot carry the PDF. <strong>“Send it for me” avoids both.</strong>
            </div>
          )}
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 8, lineHeight: 1.5 }}>
            A draft is plain text — no letterhead and no attachment. The requester is still confirmed either way.
          </div>
        </div>
      )}

      {mode === 'none' && (
        <div style={{ background: '#FAFAF8', border: `1px solid ${LINE}`, borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 12.5, color: INK, lineHeight: 1.5 }}>
          <strong>Use this when you called the order in.</strong> Nothing goes to {vendor?.name || 'the vendor'} — but the order is still recorded against this request, and {requesterName || 'the requester'} is still told it was placed.
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 6, lineHeight: 1.5 }}>
            Also the right choice if the vendor takes orders through their own portal, or you would rather write the email yourself. You can still download the PDF below to print or attach.
          </div>
        </div>
      )}

      {/* Colby's ask at 78n8.2, and it is a genuine fourth route rather than a
          convenience: the PDF is available in EVERY mode, not buried inside the
          preview. Print it, hand it over, or attach it to an email written by
          hand. Same generator as the attachment, so the file is identical
          however it leaves the building. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setShowPreview(v => !v)}
          disabled={disabled}
          style={{ flex: 1, minWidth: 130, padding: '10px 12px', borderRadius: 9, fontSize: 13, fontWeight: 600, border: `1px solid ${LINE}`, background: '#fff', color: INK, cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          {showPreview ? 'Hide preview' : 'Preview the order'}
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={disabled || pdfBusy}
          style={{ flex: 1, minWidth: 130, padding: '10px 12px', borderRadius: 9, fontSize: 13, fontWeight: 600, border: `1px solid ${LINE}`, background: '#fff', color: INK, cursor: (disabled || pdfBusy) ? 'not-allowed' : 'pointer' }}
        >
          {pdfBusy ? 'Building…' : 'Download the PDF'}
        </button>
      </div>

      {pdfError && (
        <div style={{ fontSize: 12, color: '#8A2020', marginTop: 8, lineHeight: 1.5 }}>{pdfError}</div>
      )}

      {showPreview && (
        <div style={{ marginTop: 12, border: `1px solid ${LINE}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ background: '#F5F5F3', padding: '7px 12px', fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
            Exactly what the vendor receives
          </div>
          {/* dangerouslySetInnerHTML over markup THIS APP BUILT and escaped
              itself. renderPurchaseOrderHtml() runs every person-typed value --
              the vendor name, the item names, the note -- through esc() before
              it reaches the string, and there are unit tests asserting it. It is
              also the point of previewing this way: the preview is the SAME
              markup the vendor gets, so it cannot disagree with what was sent.
              A preview drawn with separate JSX is a preview that can lie. */}
          <div style={{ padding: 14, background: '#fff', overflowX: 'auto' }}
            dangerouslySetInnerHTML={{ __html: renderPurchaseOrderHtml(order) }} />
        </div>
      )}

      {order.orderTotal > 0 && mode === 'send' && !blocked && (
        <div style={{ fontSize: 11.5, color: MUTED, marginTop: 10, lineHeight: 1.5 }}>
          {money(order.orderTotal)} order to {vendor?.name}. Nothing is sent until you confirm.
        </div>
      )}
    </div>
  );
}
